import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { API } from "../utils/helpers";

const PANELS = [
  { id: "anasayfa", label: "Ana Sayfa", url: "/", statusKey: "anasayfa" },
  { id: "admin", label: "Admin", url: "/admin", statusKey: "admin" },
  { id: "alarm", label: "Alarm Sayfasi", url: "/alarm", statusKey: "alarm" },
  { id: "dutluk", label: "Dutluk", url: "/2", statusKey: "dutluk" },
  { id: "ufuktepe", label: "Ufuktepe", url: "/1", statusKey: "ufuktepe" },
  { id: "log", label: "Admin Log", url: null, statusKey: "log" },
  { id: "cplog", label: "Kontrol Paneli Log", url: null, statusKey: "cplog" },
  { id: "anasifre", label: "Ana Sifre", url: null, statusKey: "anasifre" },
];

const ControlPanel = () => {
  const [authed, setAuthed] = useState(false);
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [tabs, setTabs] = useState([]);
  const [activeTabId, setActiveTabId] = useState(null);
  const [statuses, setStatuses] = useState({});
  const [blinking, setBlinking] = useState({});
  const [logs, setLogs] = useState([]);
  const [cpLogs, setCpLogs] = useState([]);
  const [hasAltin, setHasAltin] = useState(null);
  const tabIdCounter = useRef(0);
  const prevSnapshot = useRef({ logCount: null, alarmCount: null });
  const iframeBaseUrl = typeof window !== "undefined" ? window.location.origin : "";

  const handleLogin = async () => {
    try {
      const res = await axios.post(`${API}/auth/admin-login`, { password, source: "profekrn" });
      if (res.data.success) {
        if (res.data.type === "branch") {
          setError("Bu panele sadece master veya ana sifre ile giris yapilabilir");
          return;
        }
        setAuthed(true);
        setError("");
      } else {
        setError(res.data.message || "Giris basarisiz");
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Giris hatasi");
    }
  };

  // Auto logout after 3 hours
  useEffect(() => {
    if (!authed) return;
    const timer = setTimeout(() => setAuthed(false), 3 * 60 * 60 * 1000);
    return () => clearTimeout(timer);
  }, [authed]);

  const openTab = useCallback((panel) => {
    const id = `${panel.id}_${++tabIdCounter.current}`;
    const newTab = { ...panel, tabId: id };
    setTabs(prev => [...prev, newTab]);
    setActiveTabId(id);
  }, []);

  const closeTab = useCallback((tabId) => {
    setTabs(prev => {
      const filtered = prev.filter(t => t.tabId !== tabId);
      if (activeTabId === tabId) {
        setActiveTabId(filtered.length > 0 ? filtered[filtered.length - 1].tabId : null);
      }
      return filtered;
    });
  }, [activeTabId]);

  // Fetch statuses
  useEffect(() => {
    if (!authed) return;
    const checkStatuses = async () => {
      try {
        const [defaultRes, dutlukRes, ufuktepeRes, logsRes, alarmRes] = await Promise.all([
          axios.get(`${API}/prices/all?profile=default`).catch(() => null),
          axios.get(`${API}/prices/all?profile=2`).catch(() => null),
          axios.get(`${API}/prices/all?profile=1`).catch(() => null),
          axios.get(`${API}/admin/logs?limit=1`).catch(() => null),
          axios.get(`${API}/alarm/rules?profile=default`).catch(() => null),
        ]);

        // Update Has Altin price
        if (defaultRes?.data?.has_altin) setHasAltin(defaultRes.data.has_altin);

        const newStatuses = {};
        newStatuses.anasayfa = defaultRes?.data?.gram_altin ? "green" : "red";
        newStatuses.dutluk = dutlukRes?.data?.gram_altin ? "green" : "red";
        newStatuses.ufuktepe = ufuktepeRes?.data?.gram_altin ? "green" : "red";
        newStatuses.admin = "green";
        newStatuses.alarm = "green";
        newStatuses.log = "green";
        newStatuses.cplog = "green";

        // Detect changes for blinking
        const newBlink = {};
        const prev = prevSnapshot.current;
        const newLogCount = logsRes?.data?.total || 0;
        const triggeredAlarms = (alarmRes?.data?.rules || []).filter(r => r.triggered);
        const newAlarmCount = triggeredAlarms.length;

        if (prev.logCount !== null && newLogCount !== prev.logCount) {
          newBlink.log = true;
        }
        if (newAlarmCount > 0) {
          newBlink.alarm = true;
        }

        prev.logCount = newLogCount;
        prev.alarmCount = newAlarmCount;

        if (Object.keys(newBlink).length > 0) {
          setBlinking(b => ({ ...b, ...newBlink }));
          setTimeout(() => {
            setBlinking(b => {
              const updated = { ...b };
              Object.keys(newBlink).forEach(k => delete updated[k]);
              return updated;
            });
          }, 5000);
        }

        setStatuses(newStatuses);
      } catch { /* ignore */ }
    };
    checkStatuses();
    const interval = setInterval(checkStatuses, 5000);
    return () => clearInterval(interval);
  }, [authed]);

  // Fetch logs when log tab is active
  useEffect(() => {
    if (!authed) return;
    const activeTab = tabs.find(t => t.tabId === activeTabId);
    if (activeTab?.id === "log") {
      axios.get(`${API}/admin/logs?limit=50&source=admin`).then(res => setLogs(res.data.logs || [])).catch(() => {});
    }
    if (activeTab?.id === "cplog") {
      axios.get(`${API}/admin/logs?limit=50&source=profekrn`).then(res => setCpLogs(res.data.logs || [])).catch(() => {});
    }
  }, [authed, activeTabId, tabs]);

  if (!authed) {
    return (
      <div style={s.loginContainer}>
        <div style={s.loginBox}>
          <h2 style={s.loginTitle}>Kontrol Paneli</h2>
          <p style={{ color: "#999", marginBottom: 20 }}>QEN Ozbek Kuyumculuk</p>
          <input
            data-testid="profekrn-password"
            type="password"
            placeholder="Sifre"
            value={password}
            onChange={e => setPassword(e.target.value)}
            onKeyDown={e => e.key === "Enter" && handleLogin()}
            style={s.loginInput}
          />
          <button data-testid="profekrn-login-btn" onClick={handleLogin} style={s.loginBtn}>Giris</button>
          {error && <p data-testid="profekrn-error" style={s.errorText}>{error}</p>}
        </div>
      </div>
    );
  }

  const currentActiveTab = tabs.find(t => t.tabId === activeTabId);

  const getDotColor = (key) => {
    if (blinking[key]) return "#f59e0b";
    return statuses[key] === "red" ? "#ef4444" : "#22c55e";
  };
  const blinkAnim = (key) => blinking[key] ? { animation: "blink-dot 0.6s ease-in-out infinite" } : {};

  return (
    <div style={s.container} data-testid="profekrn-panel">
      <style>{`@keyframes blink-dot { 0%,100% { opacity: 1; } 50% { opacity: 0.2; } }`}</style>
      {/* Sidebar */}
      <div style={s.sidebar}>
        <h3 style={s.sidebarTitle}>Kontrol Paneli</h3>
        <div style={s.panelGrid}>
          {PANELS.map(panel => (
            <button
              key={panel.id}
              data-testid={`panel-btn-${panel.id}`}
              onClick={() => openTab(panel)}
              style={s.panelCard}
            >
              <span style={{ ...s.statusDot, backgroundColor: getDotColor(panel.statusKey), ...blinkAnim(panel.statusKey) }} />
              <span style={s.panelLabel}>{panel.label}</span>
            </button>
          ))}
        </div>
        {hasAltin && (
          <div style={{ margin: "12px 0", padding: "12px", border: "2px solid #DA9F54", borderRadius: 10, background: "#0f172a", textAlign: "center" }} data-testid="has-altin-box">
            <span style={{ fontSize: 11, color: "#94a3b8", display: "block", marginBottom: 4 }}>Has Altin Satis</span>
            <span style={{ fontSize: 22, fontWeight: "bold", color: "#DA9F54" }}>{hasAltin.satis ? Number(hasAltin.satis).toLocaleString("tr-TR") : "—"}</span>
            <span style={{ fontSize: 11, color: "#64748b", display: "block", marginTop: 2 }}>TL/gr</span>
          </div>
        )}
        <button
          data-testid="profekrn-logout"
          onClick={() => setAuthed(false)}
          style={s.logoutBtn}
        >
          Cikis
        </button>
      </div>

      {/* Main Area */}
      <div style={s.main}>
        {/* Tab Bar */}
        {tabs.length > 0 && (
          <div style={s.tabBar} data-testid="tab-bar">
            {tabs.map(tab => (
              <div
                key={tab.tabId}
                data-testid={`tab-${tab.tabId}`}
                onClick={() => setActiveTabId(tab.tabId)}
                style={{
                  ...s.tab,
                  backgroundColor: tab.tabId === activeTabId ? "#1e293b" : "#334155",
                  borderBottom: tab.tabId === activeTabId ? "2px solid #DA9F54" : "2px solid transparent"
                }}
              >
                <span style={{ ...s.tabDot, backgroundColor: getDotColor(tab.statusKey), ...blinkAnim(tab.statusKey) }} />
                <span style={s.tabText}>{tab.label}</span>
                <button
                  data-testid={`close-tab-${tab.tabId}`}
                  onClick={(e) => { e.stopPropagation(); closeTab(tab.tabId); }}
                  style={s.tabClose}
                >
                  x
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Content */}
        <div style={s.content}>
          {!currentActiveTab && (
            <div style={s.emptyState} data-testid="empty-state">
              <p style={{ fontSize: 18, color: "#94a3b8" }}>Bir kutucuk secin veya sekme acin</p>
            </div>
          )}

          {tabs.map(tab => (
            <div
              key={tab.tabId}
              style={{ display: tab.tabId === activeTabId ? "block" : "none", width: "100%", height: "100%" }}
            >
              {tab.id === "log" ? (
                <AdminLogs logs={logs} onRefresh={() => {
                  axios.get(`${API}/admin/logs?limit=50&source=admin`).then(res => setLogs(res.data.logs || [])).catch(() => {});
                }} />
              ) : tab.id === "cplog" ? (
                <AdminLogs logs={cpLogs} title="Kontrol Paneli Log" onRefresh={() => {
                  axios.get(`${API}/admin/logs?limit=50&source=profekrn`).then(res => setCpLogs(res.data.logs || [])).catch(() => {});
                }} />
              ) : tab.id === "anasifre" ? (
                <AnaSifrePanel />
              ) : tab.url ? (
                <iframe
                  title={tab.label}
                  src={`${iframeBaseUrl}${tab.url}`}
                  style={s.iframe}
                  data-testid={`iframe-${tab.id}`}
                />
              ) : null}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const AnaSifrePanel = () => {
  const [masterPw, setMasterPw] = useState("");
  const [newAnaSifre, setNewAnaSifre] = useState("");
  const [msg, setMsg] = useState({ text: "", type: "" });
  const [hasAnaSifre, setHasAnaSifre] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/ana-sifre`).then(res => setHasAnaSifre(res.data.exists)).catch(() => {});
  }, []);

  const handleSave = async () => {
    if (!masterPw.trim() || !newAnaSifre.trim()) { setMsg({ text: "Tum alanlari doldurun", type: "error" }); return; }
    try {
      await axios.post(`${API}/admin/ana-sifre`, { master_password: masterPw, new_password: newAnaSifre });
      setMsg({ text: "Ana sifre guncellendi!", type: "success" });
      setMasterPw(""); setNewAnaSifre(""); setHasAnaSifre(true);
      setTimeout(() => setMsg({ text: "", type: "" }), 3000);
    } catch (err) {
      setMsg({ text: err.response?.data?.detail || "Hata olustu!", type: "error" });
      setTimeout(() => setMsg({ text: "", type: "" }), 3000);
    }
  };

  return (
    <div style={s.innerPanel} data-testid="ana-sifre-panel">
      <h3 style={s.innerTitle}>Ana Sifre Yonetimi</h3>
      <p style={{ color: "#94a3b8", fontSize: 14, marginBottom: 20 }}>
        Ana sifre, admin panelinde tum subelere erisim saglar. Master sifre yetkisi vermez.
      </p>
      <div style={{ background: "#1e293b", padding: 20, borderRadius: 12, maxWidth: 400 }}>
        <p style={{ color: hasAnaSifre ? "#22c55e" : "#ef4444", fontSize: 13, marginBottom: 15, fontWeight: "bold" }}>
          {hasAnaSifre ? "Ana sifre tanimli" : "Ana sifre henuz tanimlanmadi"}
        </p>
        <input type="password" placeholder="Yeni Ana Sifre" value={newAnaSifre} onChange={e => setNewAnaSifre(e.target.value)}
          style={{ width: "100%", padding: 12, borderRadius: 8, border: "1px solid #475569", background: "#0f172a", color: "#e2e8f0", fontSize: 15, marginBottom: 10, boxSizing: "border-box" }}
          data-testid="ana-sifre-new-input" />
        <input type="password" placeholder="Master Sifre (onay icin)" value={masterPw} onChange={e => setMasterPw(e.target.value)}
          style={{ width: "100%", padding: 12, borderRadius: 8, border: "1px solid #475569", background: "#0f172a", color: "#e2e8f0", fontSize: 15, marginBottom: 15, boxSizing: "border-box" }}
          data-testid="ana-sifre-master-input" />
        <button onClick={handleSave} data-testid="ana-sifre-save-btn"
          style={{ width: "100%", padding: 12, background: "#DA9F54", color: "#000", border: "none", borderRadius: 8, cursor: "pointer", fontWeight: "bold", fontSize: 15 }}>
          {hasAnaSifre ? "Ana Sifreyi Guncelle" : "Ana Sifre Olustur"}
        </button>
        {msg.text && <p style={{ color: msg.type === "error" ? "#ef4444" : "#22c55e", marginTop: 10, fontSize: 14, textAlign: "center" }}>{msg.text}</p>}
      </div>
    </div>
  );
};

const AdminLogs = ({ logs, onRefresh, title = "Admin Log" }) => {
  const actionLabels = {
    "giris": "Admin Girisi",
    "master_giris": "Master Sifre Giris",
    "dogrulama_giris": "Dogrulama Kodu Giris",
    "3_yanlis_giris_uyari": "3 Yanlis Giris Uyarisi",
    "hesap_kilitlendi": "Hesap Kilitlendi",
    "yanlis_sifre": "Yanlis Sifre Giris Denemesi",
  };
  return (
    <div style={s.innerPanel} data-testid="admin-logs">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <h3 style={s.innerTitle}>{title}</h3>
        <button onClick={onRefresh} style={s.refreshBtn}>Yenile</button>
      </div>
      <div style={s.tableWrap}>
        <table style={s.table}>
          <thead>
            <tr>
              <th style={s.th}>Tarih</th>
              <th style={s.th}>Islem</th>
              <th style={s.th}>Profil</th>
              <th style={s.th}>Cihaz</th>
              <th style={s.th}>Konum</th>
              <th style={s.th}>IP</th>
            </tr>
          </thead>
          <tbody>
            {logs.map((log, i) => {
              const isError = ["yanlis_sifre", "3_yanlis_giris_uyari", "hesap_kilitlendi"].includes(log.action);
              return (
                <tr key={i} style={{ ...(i % 2 === 0 ? s.trEven : s.trOdd), ...(isError ? { background: "#3b1111" } : {}) }}>
                  <td style={s.td}>{log.timestamp ? new Date(log.timestamp).toLocaleString("tr-TR") : "?"}</td>
                  <td style={{ ...s.td, color: isError ? "#ef4444" : "#e2e8f0", fontWeight: isError ? "bold" : "normal" }}>{actionLabels[log.action] || log.action}</td>
                  <td style={s.td}>{log.profile || "-"}</td>
                  <td style={s.td}>{log.device?.browser || "?"} / {log.device?.os || "?"}</td>
                  <td style={s.td}>{log.location?.location || "—"}</td>
                  <td style={s.td}>{log.ip || "?"}</td>
                </tr>
              );
            })}
            {logs.length === 0 && <tr><td colSpan={6} style={{ ...s.td, textAlign: "center", color: "#999" }}>Kayit yok</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const s = {
  loginContainer: { minHeight: "100vh", background: "#0f172a", display: "flex", justifyContent: "center", alignItems: "center" },
  loginBox: { background: "#1e293b", padding: 40, borderRadius: 16, textAlign: "center", minWidth: 320, border: "1px solid #334155" },
  loginTitle: { color: "#DA9F54", fontSize: 24, marginBottom: 8 },
  loginInput: { width: "100%", padding: 14, fontSize: 16, background: "#0f172a", border: "1px solid #475569", borderRadius: 10, color: "#fff", marginBottom: 16, outline: "none", boxSizing: "border-box" },
  loginBtn: { width: "100%", padding: 14, fontSize: 16, background: "#DA9F54", color: "#fff", border: "none", borderRadius: 10, cursor: "pointer", fontWeight: "bold" },
  errorText: { color: "#ef4444", marginTop: 12, fontSize: 14 },
  container: { display: "flex", height: "100vh", background: "#0f172a", color: "#e2e8f0", fontFamily: "Inter, system-ui, sans-serif" },
  sidebar: { width: 220, background: "#1e293b", padding: 16, display: "flex", flexDirection: "column", borderRight: "1px solid #334155", flexShrink: 0 },
  sidebarTitle: { color: "#DA9F54", fontSize: 16, marginBottom: 16, textAlign: "center" },
  panelGrid: { display: "flex", flexDirection: "column", gap: 8, flex: 1 },
  panelCard: {
    display: "flex", alignItems: "center", gap: 10, padding: "12px 14px",
    background: "#334155", border: "1px solid #475569", borderRadius: 10,
    cursor: "pointer", color: "#e2e8f0", fontSize: 14, textAlign: "left",
    transition: "background 0.15s"
  },
  statusDot: { width: 10, height: 10, borderRadius: "50%", flexShrink: 0 },
  panelLabel: { flex: 1 },
  logoutBtn: { marginTop: 16, padding: 10, background: "#ef4444", color: "#fff", border: "none", borderRadius: 8, cursor: "pointer", fontSize: 13 },
  main: { flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" },
  tabBar: { display: "flex", gap: 2, background: "#0f172a", padding: "6px 8px 0", overflowX: "auto", flexShrink: 0 },
  tab: {
    display: "flex", alignItems: "center", gap: 6, padding: "8px 14px",
    borderRadius: "8px 8px 0 0", cursor: "pointer", fontSize: 13,
    color: "#e2e8f0", minWidth: 0, whiteSpace: "nowrap"
  },
  tabDot: { width: 8, height: 8, borderRadius: "50%", flexShrink: 0 },
  tabText: { overflow: "hidden", textOverflow: "ellipsis" },
  tabClose: { background: "none", border: "none", color: "#94a3b8", cursor: "pointer", fontSize: 14, padding: "0 2px", marginLeft: 4, lineHeight: 1 },
  content: { flex: 1, overflow: "hidden", position: "relative" },
  emptyState: { display: "flex", justifyContent: "center", alignItems: "center", height: "100%", flexDirection: "column" },
  iframe: { width: "100%", height: "100%", border: "none", background: "#fff" },
  innerPanel: { padding: 24, height: "100%", overflow: "auto" },
  innerTitle: { color: "#DA9F54", fontSize: 20, marginBottom: 16 },
  summaryRow: { display: "flex", gap: 16, marginBottom: 24, flexWrap: "wrap" },
  summaryCard: { background: "#1e293b", border: "1px solid #334155", borderRadius: 12, padding: "16px 24px", display: "flex", flexDirection: "column", alignItems: "center", minWidth: 100 },
  summaryNum: { fontSize: 28, fontWeight: "bold", color: "#DA9F54" },
  summaryLabel: { fontSize: 13, color: "#94a3b8", marginTop: 4 },
  tableWrap: { overflowX: "auto" },
  table: { width: "100%", borderCollapse: "collapse" },
  th: { textAlign: "left", padding: "10px 14px", borderBottom: "2px solid #334155", color: "#DA9F54", fontSize: 13, whiteSpace: "nowrap" },
  td: { padding: "10px 14px", borderBottom: "1px solid #1e293b", fontSize: 13, whiteSpace: "nowrap" },
  trEven: { background: "#0f172a" },
  trOdd: { background: "#1e293b" },
  refreshBtn: { padding: "8px 16px", background: "#334155", color: "#e2e8f0", border: "1px solid #475569", borderRadius: 8, cursor: "pointer", fontSize: 13 }
};

export default ControlPanel;
